/**
 * .tuMetrics {"tu1": 0.92, "tu2": 0.94, "tu3": 3, "notes": "Server RPCs for backplane privacy pin + desk-cleared latch", "tuVersion": "2.5"}
 * .help "Optional 4-digit backplane privacy latch stored server-side. Clearing the desk sets desk_cleared so a refresh restores the backplane (not work); unlocking verifies the code via RPC."
 */
import { supabase } from '../../lib/supabase.ts';
import { isFourDigitCode } from './backplanePinCode.ts';

export { isFourDigitCode } from './backplanePinCode.ts';

const LEGACY_STORAGE_PREFIX = 'currents.backplanePin.v1.';

/** Drop any pre-server localStorage latch for this user. */
export function clearLegacyBackplanePinLocal(userId: string | undefined | null): void {
  if (!userId || typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(`${LEGACY_STORAGE_PREFIX}${userId}`);
  } catch {
    /* ignore */
  }
}

/** True when work chrome must stay locked behind the privacy code. */
export function isBackplaneDeskLocked(
  user: { backplanePinEnabled?: boolean; backplaneDeskCleared?: boolean } | null | undefined,
): boolean {
  return !!user?.backplanePinEnabled && !!user?.backplaneDeskCleared;
}

export async function saveBackplanePin(code: string): Promise<void> {
  if (!isFourDigitCode(code)) throw new Error('Code must be 4 digits');
  const { error } = await supabase.rpc('set_backplane_pin', { p_code: code });
  if (error) throw error;
}

export async function clearBackplanePin(): Promise<void> {
  const { error } = await supabase.rpc('clear_backplane_pin');
  if (error) throw error;
}

/** Call when entering the backplane with a privacy code enabled. */
export async function markBackplaneDeskCleared(): Promise<void> {
  const { error } = await supabase.rpc('mark_backplane_desk_cleared');
  if (error) throw error;
}

/** Verify code and clear desk_cleared. Returns false on mismatch. */
export async function unlockBackplane(code: string): Promise<boolean> {
  if (!isFourDigitCode(code)) return false;
  const { data, error } = await supabase.rpc('unlock_backplane', { p_code: code });
  if (error) throw error;
  return data === true;
}

/**
 * Drop the cleared-desk latch without the privacy code.
 * Call after an explicit sign-in (password/oauth), never on bare session restore/refresh.
 */
export async function releaseBackplaneDesk(): Promise<void> {
  const { error } = await supabase.rpc('release_backplane_desk');
  if (error) throw error;
}
